import { supabase } from './serviceService.js';

// Hero/startpage content is stored as a single row (id=1).
const TABLE = 'startpage_content';
const HERO_CACHE_KEY = 'rb_hero_cache_v1';
const HERO_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
let heroCache = null;
let heroInFlightPromise = null;

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readHeroStorageCache = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(HERO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if ('headline' in parsed || 'tagline' in parsed) {
      return { content: parsed, isFresh: true };
    }
    const content = parsed.content && typeof parsed.content === 'object' ? parsed.content : null;
    const savedAt = Number(parsed.savedAt || 0);
    if (!content) return null;
    const isFresh = savedAt > 0 ? Date.now() - savedAt < HERO_CACHE_MAX_AGE_MS : true;
    return { content, isFresh };
  } catch (_) {
    return null;
  }
};

const writeHeroStorageCache = (content) => {
  if (!canUseStorage() || !content || typeof content !== 'object') return;
  try {
    window.localStorage.setItem(HERO_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      content,
    }));
  } catch (_) {
  }
};

const mapDbToModel = (row) => ({
  id: row.id,
  tagline: row.tagline ?? '',
  headline: row.headline ?? '',
  subheadline: row.subheadline ?? '',
  buttonText: row.button_text ?? row.buttonText ?? '',
  buttonHref: row.button_href ?? row.buttonHref ?? '#',
  backgroundImage: row.background_image ?? row.backgroundImage ?? '',
});

const mapModelToDb = (data) => ({
  tagline: data.tagline ?? null,
  headline: data.headline ?? null,
  subheadline: data.subheadline ?? null,
  button_text: data.buttonText ?? data.button_text ?? null,
  button_href: data.buttonHref ?? data.button_href ?? null,
  background_image: data.backgroundImage ?? data.background_image ?? null,
});

export const heroService = {
  get: async ({ force = false } = {}) => {
    if (!force && heroCache) {
      return heroCache;
    }

    if (!force) {
      const storageCache = readHeroStorageCache();
      if (storageCache?.content && storageCache.isFresh) {
        heroCache = storageCache.content;
        return storageCache.content;
      }
    }

    if (!force && heroInFlightPromise) {
      return heroInFlightPromise;
    }

    heroInFlightPromise = supabase
      .from(TABLE)
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) throw error;
        const mapped = data ? mapDbToModel(data) : null;
        heroCache = mapped;
        if (mapped) writeHeroStorageCache(mapped);
        return mapped;
      })
      .catch(() => {
        const storageCache = readHeroStorageCache();
        const fallback = storageCache?.content || null;
        heroCache = fallback;
        return fallback;
      })
      .finally(() => {
        heroInFlightPromise = null;
      });

    return heroInFlightPromise;
  },

  upsert: async (data) => {
    const payload = { id: 1, ...mapModelToDb(data) };
    const { data: result, error } = await supabase
      .from(TABLE)
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    if (error) throw error;
    const mapped = mapDbToModel(result);
    heroCache = mapped;
    writeHeroStorageCache(mapped);
    return mapped;
  },
};
